import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// 2026 Contextual images: matte finishes and office setups
const venetianImages = {
  hero: "https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&w=1200&q=80",
  office: "https://images.unsplash.com/photo-1593062096033-9a26b09da705?auto=format&fit=crop&w=600&q=80",
  bathroom: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=600&q=80",
};

function VenetianBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-slate-900">
      <Helmet>
        <title>Venetian Blinds UK | Precision Light Control & Matte Finishes</title>
        <meta
          name="description"
          content="Shop the 2026 Venetian blinds collection. Perfect for home offices and high-moisture areas. Discover cordless safety and ultra-matte aluminium slats."
        />
        <meta name="keywords" content="Aluminium Venetian Blinds UK, Home Office Blinds, Matte Black Venetian Blinds, Waterproof Bathroom Blinds, Made to Measure Venetians" />
      </Helmet>

      {/* --- Section 1: Hero - Architectural Precision --- */}
      <header className="grid lg:grid-cols-2 gap-16 items-center mb-24">
        <div>
          <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tight">
            Master Your <span className="text-emerald-700">Natural Light.</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            Venetian blinds offer a level of precision that other window treatments can't match. By adjusting the horizontal slats, you can create a <strong>"dappled sunlight"</strong> effect—perfect for indoor plants or reducing screen glare in your 2026 home office.
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="bg-slate-900 text-white px-10 py-4 rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg">
              Customize My Blinds
            </button>
            <Link to="/measuring-guide" className="px-10 py-4 border-2 border-slate-200 rounded-xl font-bold hover:bg-slate-50 transition">
              How to Measure
            </Link>
          </div>
        </div>
        <div className="relative">
          <img 
            src={venetianImages.hero} 
            alt="Modern slate grey Venetian blinds in a bright kitchen" 
            className="rounded-[2.5rem] shadow-2xl h-[500px] w-full object-cover"
          />
          <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 max-w-xs">
            <p className="text-emerald-700 font-bold text-sm uppercase mb-1">2026 Innovation</p>
            <p className="text-slate-800 font-semibold">Zero-Glare Matte Coating</p>
            <p className="text-xs text-slate-500">Optimized for video calls and eye health.</p>
          </div>
        </div>
      </header>

      {/* --- Section 2: Why Venetian? (Performance Grid) --- */}
      <section className="bg-emerald-50 rounded-[3rem] p-12 md:p-20 mb-24">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">The Versatile All-Rounder</h2>
        <div className="grid md:grid-cols-3 gap-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">💻</div>
            <h3 className="text-xl font-bold mb-3">Office Optimized</h3>
            <p className="text-slate-600">Eliminate annoying monitor reflections while keeping your space bright and productive.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">🚿</div>
            <h3 className="text-xl font-bold mb-3">Waterproof Durability</h3>
            <p className="text-slate-600">Our high-grade aluminium is rust-proof and warp-resistant—ideal for steamy bathrooms.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">🌿</div>
            <h3 className="text-xl font-bold mb-3">Biophilic Light</h3>
            <p className="text-slate-600">Tilt slats to mimic a forest canopy, providing your house plants with filtered, healthy light.</p>
          </div>
        </div>
      </section>

      {/* --- Section 3: Material Focus --- */}
      <div className="space-y-24 mb-24">
        <section className="flex flex-col md:flex-row gap-16 items-center">
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-6">Aluminium: The Sustainable Choice</h2>
            <p className="text-lg text-slate-700 mb-6">
              In 2026, sustainability is key. Our Venetian slats are crafted from <strong>recycled aluminium</strong>, offering a long-life alternative to plastics. They are fire-retardant, lightweight, and come in over 80 finishes—from burnished gold to deep forest green.
            </p>
            <Link to="/kitchen-blinds" className="text-emerald-700 font-bold hover:underline">Best for Kitchens & Bathrooms →</Link>
          </div>
          <img src={venetianImages.bathroom} alt="White Venetian blinds in a modern bathroom" className="w-full md:w-1/2 rounded-3xl shadow-lg" />
        </section>

        <section className="flex flex-col md:flex-row-reverse gap-16 items-center">
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-6">Home Office Glare Control</h2>
            <p className="text-lg text-slate-700 mb-6">
              With hybrid work now a permanent standard, the "Home Office Venetian" is our top-selling range. Unlike rollers, you can tilt the slats to direct light <em>upwards</em> toward the ceiling, illuminating your room without hitting your laptop screen.
            </p>
          </div>
          <img src={venetianImages.office} alt="Venetian blinds in a professional home office" className="w-full md:w-1/2 rounded-3xl shadow-lg" />
        </section>
      </div>

      {/* --- Section 4: 2026 Style Guide FAQ --- */}
      <section className="max-w-4xl mx-auto mb-24 border-t border-slate-100 pt-16">
        <h2 className="text-3xl font-bold mb-10 text-center">Frequently Asked Questions</h2>
        <div className="space-y-8">
          <div className="group border-b border-slate-100 pb-6">
            <h4 className="text-xl font-bold mb-2">Are cordless Venetian blinds safer?</h4>
            <p className="text-slate-600">Yes. In 2026, <strong>cordless safety</strong> is the new standard. Our systems use a simple "push-pull" bottom rail or a sleek wand mechanism, removing the risk of tangled cords for children and pets.</p>
          </div>
          <div className="group border-b border-slate-100 pb-6">
            <h4 className="text-xl font-bold mb-2">How do I clean aluminium slats?</h4>
            <p className="text-slate-600">Maintenance is effortless. Because aluminium is non-porous, a quick dust or a wipe with a damp cloth every few weeks is all that's required to keep them looking brand new.</p>
          </div>
          <div className="group border-b border-slate-100 pb-6">
            <h4 className="text-xl font-bold mb-2">Can I get a "No-Drill" version?</h4>
            <p className="text-slate-600">Absolutely. Check out our <Link to="/perfect-fit-blinds" className="text-emerald-700 underline font-semibold">Perfect Fit Venetian</Link> range which clips directly onto your window frame—perfect for renters or uPVC windows.</p>
          </div>
        </div>
      </section>

      {/* --- Section 5: Footer CTA --- */}
      <footer className="bg-slate-900 text-white p-12 md:p-20 rounded-[3rem] text-center">
        <h2 className="text-4xl font-bold mb-4">Precision Design, Made to Measure.</h2>
        <p className="mb-10 text-slate-400 max-w-xl mx-auto">From ultra-matte neutrals to bold metallic accents, find the perfect slat for your space.</p>
        
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          <Link to="/wooden-blinds" className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full transition">Switch to Real Wood</Link>
          <Link to="/vertical-blinds" className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full transition">Vertical Options</Link>
          <Link to="/home-office-blinds" className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full transition">Office Collection</Link>
        </div>

        <button className="bg-emerald-600 text-white px-12 py-5 rounded-full text-xl font-bold shadow-2xl hover:scale-105 transition-transform">
          Order 10 Free Swatches
        </button>
      </footer>
    </div>
  );
}

export default VenetianBlinds;